/**
 * G-NOMINATIM — forward place search for Create Map View wizard (`around_place`).
 * Must call shared `enqueueNominatim` (directoryGeoPlace) — never fetch off-queue.
 *
 * Pass|Fail:
 * - 0-hit → `{ kind: 'empty' }` — UI shows `place-search-empty` + i18n
 * - ≥1 hit → `{ kind: 'hits', hits }` — list + pick required (no auto-pick)
 * - network / HTTP fail → `{ kind: 'error' }`
 */
import { enqueueNominatim } from '../../shared/ui/directoryGeoPlace.js';

const NOMINATIM_SEARCH_URL = 'https://nominatim.openstreetmap.org/search';

/**
 * Czechia bounding box (left,top,right,bottom = west,north,east,south).
 * Prior Prague-metro box + bounded=1 hid Brno and other CZ cities ("No places found").
 */
export const NOMINATIM_PLACE_SEARCH_VIEWBOX = '12.09,51.06,18.87,48.55' as const;
export const NOMINATIM_PLACE_SEARCH_COUNTRYCODES = 'cz' as const;
export const NOMINATIM_PLACE_SEARCH_LIMIT = 5;

/** Legacy Prague-metro viewbox (west,north,east,south) — regression: excluded Brno. */
export const NOMINATIM_PLACE_SEARCH_VIEWBOX_PRAGUE_LEGACY =
  '14.2,50.0,14.7,50.15' as const;

export type NominatimViewbox = {
  readonly west: number;
  readonly north: number;
  readonly east: number;
  readonly south: number;
};

/**
 * Parse Nominatim `viewbox` string (west,north,east,south).
 * Returns null when the CSV is not four finite numbers.
 */
export function parseNominatimViewbox(
  viewbox: string,
): NominatimViewbox | null {
  const parts = viewbox.split(',').map((p) => Number(p.trim()));
  if (parts.length !== 4) {
    return null;
  }
  const [west, north, east, south] = parts;
  if (
    west === undefined ||
    north === undefined ||
    east === undefined ||
    south === undefined ||
    !Number.isFinite(west) ||
    !Number.isFinite(north) ||
    !Number.isFinite(east) ||
    !Number.isFinite(south)
  ) {
    return null;
  }
  return { west, north, east, south };
}

/**
 * Point-in-viewbox: west≤lng≤east and south≤lat≤north
 * (Nominatim left,top,right,bottom = west,north,east,south).
 */
export function isLatLngInNominatimViewbox(
  lat: number,
  lng: number,
  viewbox: string | NominatimViewbox,
): boolean {
  const box =
    typeof viewbox === 'string' ? parseNominatimViewbox(viewbox) : viewbox;
  if (box === null) {
    return false;
  }
  return (
    lng >= box.west &&
    lng <= box.east &&
    lat >= box.south &&
    lat <= box.north
  );
}

/** data-testid for 0-hit empty state (G-NOMINATIM Pass). */
export const PLACE_SEARCH_EMPTY_TEST_ID = 'place-search-empty' as const;

/** i18n key for 0-hit empty copy. */
export const PLACE_SEARCH_EMPTY_I18N_KEY =
  'directory.mapView.placeSearchEmpty' as const;

/** data-testid for multi-hit pick hint (G-NOMINATIM Pass). */
export const PLACE_SEARCH_MULTI_HIT_TEST_ID = 'place-search-multi-hit' as const;

/** i18n key for multi-hit pick-required copy. */
export const PLACE_SEARCH_MULTI_HIT_I18N_KEY =
  'directory.mapView.placeSearchMultiHit' as const;

/** CDP / visual Prague center check (optional helper). */
export const PRAGUE_CDP_CENTER_LAT = 50.0755;
export const PRAGUE_CDP_CENTER_LNG = 14.4378;
export const PRAGUE_CDP_DELTA = 0.3;

export type NominatimPlaceHit = {
  readonly lat: number;
  readonly lng: number;
  readonly displayName: string;
  readonly placeId: number | null;
};

/**
 * Discriminated search outcome — UI must never auto-pick first hit.
 */
export type SearchNominatimPlacesResult =
  | { readonly kind: 'empty' }
  | { readonly kind: 'hits'; readonly hits: readonly NominatimPlaceHit[] }
  | { readonly kind: 'error' };

interface NominatimSearchRawItem {
  readonly lat?: unknown;
  readonly lon?: unknown;
  readonly display_name?: unknown;
  readonly place_id?: unknown;
}

export type NominatimSearchFetcher = (
  query: string,
) => Promise<SearchNominatimPlacesResult>;

let searchFetcherOverride: NominatimSearchFetcher | null = null;

/** Blank/whitespace string → unparseable before Number (never ''→0). */
function parseCoord(raw: unknown): number | null {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null;
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
      return null;
    }
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseHit(raw: NominatimSearchRawItem): NominatimPlaceHit | null {
  const lat = parseCoord(raw.lat);
  const lng = parseCoord(raw.lon);
  if (lat === null || lng === null) {
    return null;
  }
  const displayName =
    typeof raw.display_name === 'string' ? raw.display_name.trim() : '';
  if (displayName.length === 0) {
    return null;
  }
  const placeId =
    typeof raw.place_id === 'number' && Number.isFinite(raw.place_id)
      ? raw.place_id
      : null;
  return { lat, lng, displayName, placeId };
}

/**
 * Default Nominatim forward search (browser-safe):
 * - Accept: application/json only — ban User-Agent + ban Accept-Language
 * - countrycodes=cz, Czechia viewbox + bounded=1, limit=5
 */
export const defaultNominatimSearchFetcher: NominatimSearchFetcher = async (
  query,
) => {
  const trimmed = query.trim();
  if (trimmed.length === 0) {
    return { kind: 'empty' };
  }

  const url = new URL(NOMINATIM_SEARCH_URL);
  url.searchParams.set('format', 'json');
  url.searchParams.set('q', trimmed);
  url.searchParams.set('countrycodes', NOMINATIM_PLACE_SEARCH_COUNTRYCODES);
  url.searchParams.set('viewbox', NOMINATIM_PLACE_SEARCH_VIEWBOX);
  url.searchParams.set('bounded', '1');
  url.searchParams.set('limit', String(NOMINATIM_PLACE_SEARCH_LIMIT));
  url.searchParams.set('addressdetails', '0');

  // Plan G-NOMINATIM: Accept only — never User-Agent / Accept-Language.
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  let response: Response;
  try {
    response = await fetch(url.toString(), { headers });
  } catch {
    return { kind: 'error' };
  }

  if (!response.ok) {
    return { kind: 'error' };
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { kind: 'error' };
  }

  if (!Array.isArray(body)) {
    return { kind: 'error' };
  }

  const hits: NominatimPlaceHit[] = [];
  for (const item of body) {
    if (item === null || typeof item !== 'object') {
      continue;
    }
    const hit = parseHit(item as NominatimSearchRawItem);
    if (hit !== null) {
      hits.push(hit);
    }
  }

  if (hits.length === 0) {
    return { kind: 'empty' };
  }
  // Multi-hit AND single-hit: return list — caller must require explicit pick (no auto-pick).
  return { kind: 'hits', hits };
};

function activeSearchFetcher(): NominatimSearchFetcher {
  return searchFetcherOverride ?? defaultNominatimSearchFetcher;
}

/**
 * Forward place search — always enqueued on shared Nominatim rate limiter.
 * Never auto-selects a hit; UI must list + require pick (G-NOMINATIM).
 */
export async function searchNominatimPlaces(
  query: string,
): Promise<SearchNominatimPlacesResult> {
  return enqueueNominatim(() => activeSearchFetcher()(query));
}

/**
 * Optional Prague CDP bounds / name check (G-NOMINATIM Pass):
 * `|lat−50.0755|≤0.3` AND `|lng−14.4378|≤0.3` OR display_name contains Praha/Prague.
 */
export function isPragueCdpNominatimHit(hit: {
  readonly lat: number;
  readonly lng: number;
  readonly displayName: string;
}): boolean {
  const latOk = Math.abs(hit.lat - PRAGUE_CDP_CENTER_LAT) <= PRAGUE_CDP_DELTA;
  const lngOk = Math.abs(hit.lng - PRAGUE_CDP_CENTER_LNG) <= PRAGUE_CDP_DELTA;
  if (latOk && lngOk) {
    return true;
  }
  const name = hit.displayName.toLowerCase();
  return name.includes('praha') || name.includes('prague');
}

/** Test helper — inject search port (null restores default Nominatim). */
export function setMapViewsNominatimSearchForTests(
  fetcher: NominatimSearchFetcher | null,
): void {
  searchFetcherOverride = fetcher;
}

